/**
 * Debug Store (Zustand)
 * Manages debug logs for AI agent calls.
 * Extracted from App.tsx.
 */

import { create } from 'zustand';
import { DebugLog } from '../types';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';

interface DebugState {
    debugLogs: DebugLog[];
    selectedLogId: string | null;
    showDebug: boolean;

    // Actions
    addDebugLog: (
        label: string,
        systemPrompt: string,
        input: any,
        updateId?: string,
        fileId?: string
    ) => string;
    loadLogs: () => Promise<void>;
    clearLogs: () => void;
    selectLog: (logId: string | null) => void;
    toggleDebugPanel: () => void;
    setShowDebug: (show: boolean) => void;

    // Selectors
    getLogsByFileId: (fileId: string) => DebugLog[];
}

export const useDebugStore = create<DebugState>((set, get) => ({
    debugLogs: [],
    selectedLogId: null,
    showDebug: false,

    addDebugLog: (label, systemPrompt, dataOrUpdate, updateId, fileId) => {
        // Robustly find the ID - support 'updateId' passed as param OR inside payload
        const id = updateId || (dataOrUpdate && (dataOrUpdate.id || dataOrUpdate.logId));

        if (id) {
            let updatedLog: DebugLog | null = null;
            set(state => ({
                debugLogs: state.debugLogs.map(log => {
                    if (log.id !== id) return log;

                    // Extract output fields from data - DO NOT overwrite original input
                    const { OUTPUT, output, rawOutput, rawText, error, usage } = dataOrUpdate || {};
                    const newLog = {
                        ...log,
                        systemPrompt: systemPrompt || log.systemPrompt,
                        // Keep original input, only add usage to it if present
                        input: usage ? { ...(log.input as any), usage } : log.input,
                        output: OUTPUT || output,
                        rawOutput: rawOutput || rawText, // Store raw CSV/text response
                        error: error
                    };
                    updatedLog = newLog;
                    return newLog;
                })
            }));

            // Sync with file system
            if (updatedLog) {
                fileSystemPersistence.saveLog(updatedLog);
            }
            return id as string;
        }

        const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const log: DebugLog = {
            id: newId,
            fileId,
            timestamp: new Date(),
            label,
            systemPrompt,
            input: dataOrUpdate
        };

        set(state => ({
            debugLogs: [log, ...state.debugLogs].slice(0, 50),
            selectedLogId: newId
        }));

        // Sync with file system
        fileSystemPersistence.saveLog(log);

        return newId;
    },

    loadLogs: async () => {
        const logs = await fileSystemPersistence.loadLogs();
        const mappedLogs = logs.map(log => ({
            ...log,
            timestamp: log.timestamp ? new Date(log.timestamp) : new Date()
        }));
        set({ debugLogs: mappedLogs });
    },

    clearLogs: () => set({ debugLogs: [], selectedLogId: null }),

    selectLog: (logId) => set({ selectedLogId: logId }),

    toggleDebugPanel: () => set(state => ({ showDebug: !state.showDebug })),

    setShowDebug: (show) => set({ showDebug: show }),

    getLogsByFileId: (fileId) => {
        return get().debugLogs.filter(log => log.fileId === fileId);
    }
}));
